import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Sparkles } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useReportConversation } from '../hooks/useReportConversation';
import { Composer } from './Composer';
import { riseSx } from './motion';
import { ReportAnswer } from './ReportAnswer';
import { UserMessage } from './UserMessage';

type Props = {
  reportId: string;
  title: string;
  /** The report's main instrument, for a suggestion about its view. */
  instrument?: string | undefined;
  onPage: (page: number) => void;
};

/** Ask on a report page: a conversation answered from this report alone, every point linked to its page. */
export function ReportAsk({ reportId, title, instrument, onPage }: Props) {
  const { turns, send, stop, isBusy } = useReportConversation(reportId);
  const endRef = useRef<HTMLDivElement>(null);
  const last = turns.at(-1);
  const suggestions = [
    'What are the key calls?',
    'What does this report say about the outlook?',
    instrument ? `What are the risks to the ${instrument} view?` : 'What are the risks?',
    'Why does the analyst think this?',
  ];

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [turns.length, last?.steps.length, last?.draft, last?.answer]);

  return (
    <Stack sx={{ height: '100%', minHeight: 0 }}>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, py: 2.5 }} aria-live="polite">
        {turns.length === 0 ? (
          <Stack sx={{ gap: 1.5, ...riseSx }}>
            <Box aria-hidden sx={markSx}><Sparkles size={15} strokeWidth={2} /></Box>
            <Box>
              <Typography variant="h4" component="h2">Ask anything about this report</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Answers use only "{title}" and jump to the exact page.</Typography>
            </Box>
            <Stack sx={{ gap: 0.75 }}>
              {suggestions.map((q, i) => (
                <ButtonBase key={q} onClick={() => { send(q); }} sx={{ ...suggestionSx, animationDelay: `${60 + i * 40}ms` }}>{q}</ButtonBase>
              ))}
            </Stack>
          </Stack>
        ) : (
          <Stack sx={{ gap: 2.5 }}>
            {turns.map((t) => (
              <Stack key={t.id} component="article" aria-label={`Question: ${t.question}`} sx={{ gap: 1.75 }}>
                <UserMessage text={t.question} />
                <ReportAnswer turn={t} onPage={onPage} />
              </Stack>
            ))}
            <div ref={endRef} />
          </Stack>
        )}
      </Box>
      <Box sx={{ p: 1.5, pt: 1 }}>
        <Composer isBusy={isBusy} onSend={send} onStop={stop} placeholder="Ask about this report" hint="Scoped to this report" />
      </Box>
    </Stack>
  );
}

const markSx = { width: 30, height: 30, borderRadius: 1, display: 'grid', placeItems: 'center', bgcolor: 'primary.main', color: 'primary.contrastText' } as const;

const suggestionSx = {
  justifyContent: 'flex-start',
  textAlign: 'left',
  px: 1.5,
  py: 1.125,
  fontSize: '0.8125rem',
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  bgcolor: 'background.paper',
  transition: 'border-color 150ms, background-color 150ms',
  '&:hover': { borderColor: 'surface.border', bgcolor: 'surface.sidebar' },
  '@keyframes suggestIn': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1, transform: 'none' } },
  animation: 'suggestIn 240ms ease-out both',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
} as const;
